import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Wrapper from '../../components/Wrapper'
import { useNavigate, useParams } from 'react-router-dom'
import { Typography, Box, Paper, IconButton, TextField, Autocomplete, Grid, Alert, Button, ToggleButton, ToggleButtonGroup, ListSubheader } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import InfoIcon from '@mui/icons-material/Info'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { menu, group, weekDays, input, ivrSingle, readyShortName, ivrNamePresets, ivr, short, lang, shortFunctionList } from '../../translate'
import Api from '../../Api'
import Cookies from 'js-cookie'
import { greenTheme, sectionTitleStyles, groupPageStyles, titleTypographyStyles, customStyles } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const primaryColor = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover
const themeColors = { primary: primaryColor, hover: hoverColor }
const inputStylesLocal = {
    '& .MuiOutlinedInput-root': { minHeight: '41px', '& fieldset': { borderColor: primaryColor }, '&:hover fieldset': { borderColor: hoverColor }, '&.Mui-focused fieldset': { borderColor: primaryColor } },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
}
const inputLabelStylesLocal = { '&.Mui-focused': { color: primaryColor } }
const selectStylesLocal = {
    '& .MuiOutlinedInput-root': { minHeight: '41px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: hoverColor },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
}
const customStylesLocal = {
    ...customStyles,
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? primaryColor : base.borderColor,
        boxShadow: state.isFocused ? `0 0 0 1px ${primaryColor}` : base.boxShadow,
        '&:hover': { borderColor: primaryColor },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: (state.isSelected || state.isFocused) ? `${primaryColor}26` : base.backgroundColor,
        color: '#000',
        border: 'none',
        borderTop: 'none',
        borderBottom: 'none',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
    }),
}
import { DndContext, closestCorners, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import FileCard from '../../components/new_component/FileCard'
import { toast } from 'react-toastify'
import CreatableSelect from 'react-select/creatable'
import { assignShortNumbersTree, defaultMenuTree, normalizeMenuTree } from '../../components/new_component/ivr/ivrMenuTreeUtils'
import { IvrMenuBranch } from '../../components/new_component/ivr/IvrMenuBuilder'

/** Member IDs allowed to switch between IVR and Menu editor modes. */

export default function IvrSingle() {
    const langIndex = +localStorage.getItem('atsLang') || 1
    const { id } = useParams()
    const navigate = useNavigate()
    const userData = useSelector((state) => state.users.userData) || {}
    const canShowMenuIvrToggle = true
    const [form, setForm] = useState({
        shortNumberName: '',
        shortNumber: '',
        noAnswerShort: '',
        is_order: 0,
        send_mail: [],
        is_menu: 0,
        menu: [],
        files: [{
            id: Date.now(),
            file: '',
            rx: '0',
            tx: '0',
            startDay: '2',
            endDay: '1',
            startHour: '00',
            startMinute: '00',
            endHour: '23',
            endMinute: '59',
            is_order: 0,
        }],
    })
    const [shortNumbers, setShortNumbers] = useState([])
    /** Employees with internal short (for menu Connect only). */
    const [menuConnectEmployees, setMenuConnectEmployees] = useState([])
    const [expandedNoAnswerGroups, setExpandedNoAnswerGroups] = useState({ group: true, ivr: true, employee: true })
    const nameOptions = useMemo(() => {
        const fromReady = Object.values(readyShortName).map(option => option[langIndex] || option[1])
        const fromIvrPresets = Object.values(ivrNamePresets).map(option => option[langIndex] || option[1])
        return [...fromIvrPresets, ...fromReady]
    }, [langIndex])

    const noAnswerSelectOptions = useMemo(() => {
        const empVirtuals = new Set(menuConnectEmployees.map((e) => String(e.virtual)))
        // Prefer employee short over a group short with the same number
        const filteredShorts = shortNumbers.filter((s) => {
            if (String(s.virtual) === '568898' || String(s.virtual) === '789989' || Number(s.autostatus) === 25) return false
            if ((s.isivr ?? 0) === 0 && empVirtuals.has(String(s.virtual))) return false
            return true
        })
        const activated = []
        if (shortNumbers.some((s) => String(s.virtual) === '568898')) {
            const label = shortFunctionList?.['568898']?.[langIndex] || 'Call center'
            activated.push({ id: 'fn-568898', virtual: '568898', name: label, label, isivr: -1 })
        }
        const familiarIndex = filteredShorts.findIndex((f) => String(f.virtual) === '10999')
        if (familiarIndex >= 0) filteredShorts.splice(familiarIndex + 1, 0, ...activated)
        else filteredShorts.unshift(...activated)
        return [...filteredShorts, ...menuConnectEmployees]
    }, [shortNumbers, menuConnectEmployees, langIndex])

    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const emailTlds = ['.com', '.ru', '.am']
    const [emailInput, setEmailInput] = useState('')

    useEffect(() => {
        (async () => {
            try {
                setLoading(true)
                if (id !== 'add') {
                    const response = await Api.groupsGetSingle(id)
                    if (response?.data?.status === 'ok') {
                        let single = response.data.data
                        let filesData = (single.item || []).map(item => {
                            const daysParts = item.days ? item.days.split('-') : ['2', '1']
                            const hoursParts = item.hours ? item.hours.split(/[:-]/) : ['00', '00', '23', '59']
                            return {
                                id: item.id || Date.now(),
                                file: item.buddy || '',
                                rx: item.rx || '',
                                tx: item.tx || '',
                                startDay: daysParts[0] || '2',
                                endDay: daysParts[1] || '1',
                                startHour: hoursParts[0] || '00',
                                startMinute: hoursParts[1] || '00',
                                endHour: hoursParts[2] || '23',
                                endMinute: hoursParts[3] || '59',
                                is_order: item.is_order || 0,
                            }
                        })
                        let noAnswerValue = ''
                        if (single.noaction !== null && single.noaction !== undefined && single.noaction !== '') {
                            const matchingOption = shortNumbers.find(s => s.virtual === String(single.noaction))
                            noAnswerValue = matchingOption || String(single.noaction)
                        }
                        const sendMailArray = single.send_mail
                            ? (Array.isArray(single.send_mail) ? single.send_mail : single.send_mail.split(',').map((m) => m.trim()).filter(Boolean))
                            : []
                        setForm({
                            shortNumberName: single.name || '',
                            shortNumber: single.virtual !== null && single.virtual !== undefined ? String(single.virtual) : '',
                            noAnswerShort: noAnswerValue,
                            is_order: single.is_order || 0,
                            send_mail: sendMailArray,
                            is_menu: single.is_menu != null && !Number.isNaN(Number(single.is_menu)) ? Number(single.is_menu) : 0,
                            menu: (() => {
                                const rows = Array.isArray(single.menu) ? single.menu : []
                                const im = single.is_menu != null && !Number.isNaN(Number(single.is_menu)) ? Number(single.is_menu) : 0
                                if (im === 1 && rows.length === 0) return defaultMenuTree()
                                return normalizeMenuTree(rows)
                            })(),
                            files: filesData.length > 0 ? filesData : [{
                                id: Date.now(),
                                file: '',
                                rx: '0',
                                tx: '0',
                                startDay: '2',
                                endDay: '1',
                                startHour: '00',
                                startMinute: '00',
                                endHour: '23',
                                endMinute: '59',
                                is_order: 0,
                            }],
                        })
                    }
                }
            } catch (error) {
                console.error('Error fetching IVR group:', error)
            } finally {
                setLoading(false)
            }
        })()
    }, [id, shortNumbers])
    
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        })
    )

    useEffect(() => {
        fetchShortNumbers()
        fetchFiles()
    }, [])

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const empRes = await Api.employeeGetEmployees().catch(() => ({ data: { status: 'error' } }))
                if (cancelled) return

                const rows =
                    empRes?.data?.status === 'ok' && Array.isArray(empRes.data.employees) ? empRes.data.employees : []

                const seen = new Set()
                const out = []
                for (const e of rows) {
                    const shortRaw = e?.short
                    const shortVal = shortRaw == null || shortRaw === '' ? '' : String(shortRaw).trim()
                    if (!shortVal) continue
                    if (seen.has(shortVal)) continue
                    seen.add(shortVal)
                    const buddyVal = e?.buddy != null ? String(e.buddy).trim() : ''
                    const notes = (e.notes && String(e.notes).trim()) || buddyVal || ''
                    out.push({
                        id: `emp-${buddyVal || shortVal}`,
                        virtual: shortVal,
                        name: notes,
                        label: notes ? `${shortVal} - ${notes}` : shortVal,
                        ivrMenuConnectSection: 'employee',
                    })
                }

                if (!cancelled) setMenuConnectEmployees(out)
            } catch {
                if (!cancelled) setMenuConnectEmployees([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const fetchShortNumbers = async () => {
        setLoading(true)
        try {
            const [groupsResponse, shortNumbersRes] = await Promise.all([
                Api.groupsGet(1),
                Api.shortGetShortNumbers().catch(() => ({ data: { data: [] } })),
            ])
            const fnList = shortNumbersRes?.data?.data || []
            const functionCodeOptions = fnList.map((l) => {
                const code = String(l.function_code ?? l.code ?? '')
                const label = l[lang[langIndex]] || l[lang[1]] || code
                return {
                    id: `fn-${code}`,
                    virtual: code,
                    name: label,
                    label,
                    isivr: -1,
                }
            })
            let shortGroups = []
            if (groupsResponse?.data?.status === 'ok') {
                shortGroups = groupsResponse.data.data || []
                const groupOptions = shortGroups.map(g => ({
                    id: g.id,
                    virtual: g.virtual,
                    name: g.name || `Short ${g.virtual}`,
                    label: `${g.virtual}${g.name ? ` - ${g.name}` : ''}`,
                    isivr: g.isivr ?? 0,
                    autostatus: g.autostatus,
                }))
                const allShortNumbers = [...functionCodeOptions.filter((fc) => fc.virtual), ...groupOptions]
                setShortNumbers(allShortNumbers)
            } else {
                setShortNumbers(functionCodeOptions.filter((fc) => fc.virtual))
            }
        } catch (error) {
            console.error('Error fetching short numbers:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchFiles = async () => {
        try {
            const response = await Api.fileGet(true)
            if (response?.data?.status === 'ok') {
                const fileList = response.data?.file || []
                setFiles(fileList.map(f => ({
                    id: f.id,
                    fname: f.fname,
                    name: f.name,
                    label: `${f.name} (${f.fname})`,
                })))
            }
        } catch (error) {
            console.error('Error fetching files:', error)
        }
    }

    const handleGoBack = () => {
        navigate('/ivrs')
    }

    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

    const handleSendMailChange = (values) => {
        const emails = values?.map(v => v.value) || []
        setForm((prev) => ({
            ...prev,
            send_mail: emails
        }))
        setEmailInput('')
    }

    const handleCreateEmail = (value) => {
        const trimmed = value.trim()
        const candidate = (() => {
            if (isValidEmail(trimmed)) return trimmed
            if (!trimmed.includes('@')) return ''
            const hasTld = /\.[a-zA-Z]{2,}$/.test(trimmed)
            if (hasTld) return ''
            const fallback = `${trimmed}.com`
            return isValidEmail(fallback) ? fallback : ''
        })()
        if (!candidate) {
            toast.error('Enter a valid email')
            return
        }
        handleSendMailChange([...(form.send_mail || []).map(e => ({value: e, label: e})), {
            value: candidate,
            label: candidate
        }])
    }

    const emailOptions = useMemo(() => {
        const selected = (Array.isArray(form.send_mail) ? form.send_mail : [])
            .map(email => ({value: email, label: email}))
        const trimmed = emailInput.trim()
        const hasAt = trimmed.includes('@')
        const hasTld = /\.[a-zA-Z]{2,}$/.test(trimmed)
        const suggestions = hasAt && !hasTld
            ? emailTlds
                .map(tld => {
                    const value = `${trimmed}${tld}`
                    return {value, label: value}
                })
                .filter(opt => !selected.find(sel => sel.value === opt.value))
            : []
        return [...suggestions, ...selected]
    }, [emailInput, form.send_mail])

    const handleSave = async () => {
        try {
            const payload = { ...form, menu: assignShortNumbersTree(form.menu) }
            if (id === 'add' && Number(form.is_menu) === 1) {
                const response = await Api.ivrsCreateMenu(payload)
                if (response?.data?.status === 'ok') {
                    navigate('/ivrs')
                }
            } else if (id === 'add') {
                const response = await Api.ivrsCreate(form)
                if (response?.data?.status === 'ok') {
                    navigate('/ivrs')
                }
            } else {
                const payload =
                    Number(form.is_menu) === 1
                        ? { ...form, menu: assignShortNumbersTree(form.menu || []) }
                        : form
                const response = await Api.ivrsUpdate(id, payload)
                if (response?.data?.status === 'ok') {
                    navigate('/ivrs')
                }
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error saving IVR group')
            console.error('Error saving IVR group:', error)
        }
    }

    const handleEditorMode = (_, value) => {
        if (value == null) return
        setForm((prev) => {
            const isMenu = value === 'menu'
            return {
                ...prev,
                is_menu: isMenu ? 1 : 0,
                menu: isMenu
                    ? (Array.isArray(prev.menu) && prev.menu.length > 0 ? prev.menu : defaultMenuTree())
                    : prev.menu,
            }
        })
    }

    const isMenuMode = Number(form.is_menu) === 1
    const showMenuEditor = canShowMenuIvrToggle && isMenuMode

    const ivrMenuEditorCtx = useMemo(
        () => ({
            setForm,
            langIndex,
            nameOptions,
            shortNumbers,
            menuConnectEmployees,
            loading,
            filesList: files,
            sensors,
            themeColors,
            inputStylesLocal,
            inputLabelStylesLocal,
            selectStylesLocal,
            group,
            weekDays,
            input,
            ivrSingle,
            primaryColor,
            hoverColor,
            groupPageStyles,
            sectionTitleStyles,
        }),
        [
            langIndex,
            nameOptions,
            shortNumbers,
            menuConnectEmployees,
            loading,
            files,
            sensors,
            themeColors,
            group,
            weekDays,
            input,
            ivrSingle,
            primaryColor,
            hoverColor,
        ]
    )

    const filesSection = (
        <Box sx={groupPageStyles.sectionBox}>
            <Box sx={groupPageStyles.employeeSectionHeader}>
                <Typography variant="h6" sx={sectionTitleStyles}>
                    {ivrSingle.files_section[langIndex]}
                </Typography>
            </Box>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragEnd={(event) => {
                    const { active, over } = event
                    if (over && active.id !== over.id) {
                        const oldIndex = form.files.findIndex(file => (file.id || form.files.indexOf(file)) === active.id)
                        const newIndex = form.files.findIndex(file => (file.id || form.files.indexOf(file)) === over.id)
                        if (oldIndex !== -1 && newIndex !== -1) {
                            setForm({
                                ...form,
                                files: arrayMove(form.files, oldIndex, newIndex)
                            })
                        }
                    }
                }}
            >
                <SortableContext
                    items={form.files.map(file => file.id || form.files.indexOf(file))}
                    strategy={verticalListSortingStrategy}
                >
                    {form.files.map((file, index) => (
                        <FileCard
                            key={`file-${index}`}
                            file={file}
                            index={index}
                            files={files}
                            form={form}
                            setForm={setForm}
                            langIndex={langIndex}
                            greenTheme={themeColors}
                            inputStyles={inputStylesLocal}
                            inputLabelStyles={inputLabelStylesLocal}
                            selectStyles={selectStylesLocal}
                            group={group}
                            weekDays={weekDays}
                            input={input}
                            isOrdered={form.is_order === 1}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <Button
                variant="contained"
                size="small"
                onClick={() => {
                    setForm({
                        ...form,
                        files: [
                            ...form.files,
                            {
                                id: Date.now(),
                                file: '',
                                rx: '0',
                                tx: '0',
                                startDay: '2',
                                endDay: '1',
                                startHour: '00',
                                startMinute: '00',
                                endHour: '23',
                                endMinute: '59',
                                is_order: 0,
                            },
                        ],
                    })
                }}
                sx={{
                    ...groupPageStyles.addEmployeeButton,
                    mb: 2,
                    backgroundColor: primaryColor,
                    color: '#fff',
                    '&:hover': { backgroundColor: hoverColor },
                }}
            >
                {ivrSingle.add_employee[langIndex]}
            </Button>
        </Box>
    )

    const sendMailSection = (
        <Box sx={groupPageStyles.sectionBox}>
            <Typography variant="h6" sx={sectionTitleStyles}>
                {ivr.send_mail[langIndex]}
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                    <CreatableSelect
                        isMulti
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={customStylesLocal}
                        menuPortalTarget={document.body}
                        placeholder="email@example + choose .com/.ru/.am"
                        value={(Array.isArray(form.send_mail) ? form.send_mail : []).map((email) => ({
                            label: email,
                            value: email,
                        }))}
                        options={emailOptions}
                        onChange={handleSendMailChange}
                        onCreateOption={handleCreateEmail}
                        onInputChange={(val) => setEmailInput(val)}
                    />
                </Grid>
            </Grid>
        </Box>
    )

    return (
        <Wrapper>
            <div className="tableParent">
                <Paper sx={groupPageStyles.paper}>
                    <Box sx={groupPageStyles.headerBox}>
                        <IconButton
                            size="small"
                            sx={{ color: primaryColor, fontSize: 18, '&:hover': { backgroundColor: `${primaryColor}15` } }}
                            onClick={handleGoBack}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewIcon sx={groupPageStyles.backIcon} />
                        </IconButton>
                        <Typography variant="h5" sx={titleTypographyStyles}>
                            {menu.ivr[langIndex]}
                        </Typography>
                    </Box>

                    <Alert
                        icon={<InfoIcon />}
                        severity="success"
                        sx={
                            REACT_APP_SERVER !== 'ATS'
                                ? { mb: 3 }
                                : {
                                    mb: 3,
                                    border: `1px solid ${primaryColor}`,
                                    backgroundColor: `${primaryColor}10`,
                                    '& .MuiAlert-icon': { color: primaryColor },
                                }
                        }
                    >
                        <Typography variant="body2">
                            {ivrSingle.info_text[langIndex]}
                        </Typography>
                    </Alert>

                    <Paper sx={{ p: 3, border: `1px solid ${primaryColor}` }}>
                        <Box sx={groupPageStyles.sectionBox}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                                <Typography variant="h6" sx={sectionTitleStyles}>
                                    {ivrSingle.group_information[langIndex]}
                                </Typography>
                                {canShowMenuIvrToggle ? (
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={isMenuMode ? 'menu' : 'ivr'}
                                        onChange={handleEditorMode}
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                px: 2,
                                                textTransform: 'none',
                                                borderColor: primaryColor,
                                                color: primaryColor,
                                                '&.Mui-selected': {
                                                    color: '#fff',
                                                    backgroundColor: `${primaryColor} !important`,
                                                },
                                            },
                                        }}
                                    >
                                        <ToggleButton value="ivr" aria-label="IVR">
                                            {ivrSingle.editor_mode_ivr[langIndex]}
                                        </ToggleButton>
                                        <ToggleButton value="menu" aria-label="Menu">
                                            {ivrSingle.editor_mode_menu[langIndex]}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                ) : null}
                            </Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={nameOptions}
                                        value={form.shortNumberName}
                                        onChange={(event, newValue) => {
                                            const value = typeof newValue === 'string' ? newValue : newValue?.value || ''
                                            setForm({ ...form, shortNumberName: value })
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setForm({ ...form, shortNumberName: newInputValue })
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={ivrSingle.short_number_name_label[langIndex]}
                                                placeholder={ivrSingle.short_number_name_placeholder[langIndex]}
                                                size="small"
                                                sx={inputStylesLocal}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={ivrSingle.short_number_label[langIndex]}
                                        placeholder={ivrSingle.short_number_placeholder[langIndex]}
                                        value={form.shortNumber}
                                        onChange={(e) => setForm({ ...form, shortNumber: e.target.value })}
                                        variant="outlined"
                                        sx={inputStylesLocal}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={noAnswerSelectOptions}
                                        groupBy={(option) => {
                                            if (typeof option === 'string' || !option) return ''
                                            if (option.ivrMenuConnectSection === 'employee') return menu.worker[langIndex]
                                            if (option.isivr === 1) return short.ivr_lists[langIndex]
                                            if (option.isivr === 0) return short.group[langIndex]
                                            return short.no_answer[langIndex]
                                        }}
                                        renderGroup={(params) => {
                                            const isNoAnswer = params.group === short.no_answer[langIndex]
                                            if (params.group === '' || isNoAnswer) {
                                                return <li key={params.key}><ul style={groupPageStyles.listUl}>{params.children}</ul></li>
                                            }
                                            const groupKey =
                                                params.group === menu.worker[langIndex]
                                                    ? 'employee'
                                                    : params.group === short.group[langIndex]
                                                      ? 'group'
                                                      : 'ivr'
                                            const expanded = expandedNoAnswerGroups[groupKey] !== false
                                            return (
                                                <li key={params.key}>
                                                    <ListSubheader
                                                        component="div"
                                                        sx={groupPageStyles.listSubheader}
                                                        onClick={() => setExpandedNoAnswerGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                                    >
                                                        {expanded ? <ExpandLessIcon sx={groupPageStyles.expandIcon} /> : <ExpandMoreIcon sx={groupPageStyles.expandIcon} />}
                                                        {params.group}
                                                    </ListSubheader>
                                                    {expanded && <ul style={groupPageStyles.listUl}>{params.children}</ul>}
                                                </li>
                                            )
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option
                                            if (!option) return ''
                                            return option.label || `${option.virtual}`
                                        }}
                                        value={(() => {
                                            if (!form.noAnswerShort) return null
                                            if (typeof form.noAnswerShort === 'object') return form.noAnswerShort
                                            const matchingOption = noAnswerSelectOptions.find((s) => s.virtual === String(form.noAnswerShort))
                                            return matchingOption || form.noAnswerShort
                                        })()}
                                        onChange={(event, newValue) => {
                                            setForm({ ...form, noAnswerShort: newValue || '' })
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            if (newInputValue && !noAnswerSelectOptions.find((s) => s.virtual === newInputValue || s.label === newInputValue)) {
                                                setForm({ ...form, noAnswerShort: newInputValue })
                                            }
                                        }}
                                        isOptionEqualToValue={(option, value) => {
                                            if (typeof option === 'string' && typeof value === 'string') return option === value
                                            if (typeof option === 'object' && typeof value === 'object') return option.virtual === value.virtual
                                            if (typeof option === 'object' && typeof value === 'string') return option.virtual === value
                                            if (typeof option === 'string' && typeof value === 'object') return option === value.virtual
                                            return false
                                        }}
                                        loading={loading}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={ivrSingle.no_answer_short_label[langIndex]}
                                                placeholder={ivrSingle.no_answer_short_placeholder[langIndex]}
                                                size="small"
                                                sx={inputStylesLocal}
                                                InputLabelProps={{ sx: inputLabelStylesLocal }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {showMenuEditor ? filesSection : null}

                        {showMenuEditor ? (
                            <Box sx={groupPageStyles.sectionBox}>
                                <Typography variant="h6" sx={sectionTitleStyles}>
                                    {ivrSingle.menu_builder_title[langIndex]}
                                </Typography>
                                <IvrMenuBranch items={Array.isArray(form.menu) ? form.menu : []} parentPath={[]} depth={0} ctx={ivrMenuEditorCtx} />
                            </Box>
                        ) : null}

                        {showMenuEditor ? (
                            <Box sx={{ ...groupPageStyles.sectionBox, mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {ivrSingle.child_hint_title[langIndex]}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {ivrSingle.child_hint[langIndex]}
                                </Typography>
                            </Box>
                        ) : null}

                        {!showMenuEditor ? sendMailSection : null}

                        {!showMenuEditor ? filesSection : null}

                        <Box sx={groupPageStyles.saveButtonBox}>
                            <Button
                                variant="contained"
                                disabled={false}
                                size="small"
                                onClick={handleSave}
                                sx={{
                                    backgroundColor: primaryColor,
                                    color: '#fff',
                                    px: 3,
                                    py: 0.75,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minHeight: '41px',
                                    '&:hover': { backgroundColor: hoverColor },
                                }}
                            >
                                {ivrSingle.save_button[langIndex]}
                            </Button>
                        </Box>
                    </Paper>
                </Paper>
            </div>
        </Wrapper>
    )
}
